import { useState } from "react";
import { classMerge } from "../../utility";
import styles from "./ArsListing.module.css";
import { useArsListing } from "./ArsListingContextProvider";
import { ARSL_APPLY_DROPPED_SORT_COLUMNS } from "./interface";

export interface ArsListingColumnDropSortVariableTargetProps {
  colId: string;
  addVarIx: number;
}

/**
 * Target div for ArsSidebarItemVariable[] drop to add sort columns to the listing.
 *
 * @param props - Component props
 * @param props.colId - ID of the column where the dropped sort columns should be inserted
 * @param props.addVarIx - Index of the variable in the column where the dropped sort columns should be inserted
 * @returns React node representing the drop target
 */
export const ArsListingColumnDropSortVariableTarget = <T,>({
  colId,
  addVarIx,
}: ArsListingColumnDropSortVariableTargetProps): React.ReactNode => {
  const { state, dispatch } = useArsListing<T>();
  const [isBeingDraggedOver, setIsBeingDraggedOver] = useState(false);
  if (!state) return null;

  /**
   * Handles the drop event when sort columns are dropped onto the target div.
   *
   * @param e - Drag event
   */
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "none";
    const jsonPayLoad = e.dataTransfer.getData("application/json");
    setIsBeingDraggedOver(false);
    dispatch({
      operation: ARSL_APPLY_DROPPED_SORT_COLUMNS,
      colId,
      variableIndex: addVarIx,
      jsonPayload: jsonPayLoad,
    });
  };

  /**
   * Handles the drag over event to allow dropping of sort columns onto the target div.
   *
   * @param e - Drag event
   */
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setIsBeingDraggedOver(true);
  };

  /**
   * Handle the drag leave event to reset the drag over state when the dragged item leaves the target div.
   *
   * @param e - Drag event
   */
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "none";
    setIsBeingDraggedOver(false);
  };

  return (
    <div
      id={`ars-listing-column-drop-sort-variable-target-${colId}-${addVarIx}`}
      className={classMerge([
        styles.variableDropSortTarget,
        isBeingDraggedOver ? styles.variableDropSortTargetActive : "",
      ])}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    />
  );
};

ArsListingColumnDropSortVariableTarget.displayName = "ArsListingColumnDropSortVariableTarget";
